import {CommentsCategory} from '@domain/comments'
import {DataContentWrapper} from '@hconnect/common/containers'
import {SapStatusCode} from '@hconnect/common/types'
import {useUpmPlant} from '@hconnect/upm'
import {useHasPermission} from '@hproduce/permissions'
import moment from 'moment-timezone'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  MachineSentryActionDetails,
  MachineSentryActionMetaAnnotations,
  TaskCreateMaintenanceNotificationIcon,
  TaskDetailsCardLayout,
  TaskEditIcon,
  TaskMenuActions,
  type TaskMenuOption
} from '../../components'
import {MachineSentryCommentsQueryKeyPrefix, FEATURE_NAMESPACE} from '../../consts'
import {
  useCreateMachineSentryActionMaintenanceNotification,
  useMachineSentryAction
} from '../../hooks'
import {HProduceTaskIdentifiers, HProduceTask} from '../../types'
import {CommentsContainer} from '../CommentsContainer'

import {MachineSentryCreateMaintenanceNotificationDialog} from './MachineSentryCreateMaintenanceNotificationDialog'
import {
  hasMaintenanceNotificationCreationTimedOut,
  MachineSentrySapMaintenanceNotificationDetails
} from './MachineSentrySapMaintenanceNotificationDetails'

type Props = {
  task: HProduceTaskIdentifiers
  onClose: (original?: HProduceTask, editVersion?: HProduceTask) => void
  onEditTaskClick: (taskId: string) => void
}

export const MachineSentryActionDetailsContainer = ({task, onClose, onEditTaskClick}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const upmPlant = useUpmPlant({plantId: task.plantId, shouldRaiseError: false})
  const {data, isLoading, isError, refetch, isRefetching} = useMachineSentryAction({
    plantUpmId: upmPlant?.upmId,
    externalId: task.externalId
  })
  const hasPermission = useHasPermission({
    plantId: task.plantId,
    countryId: upmPlant?.country ?? ''
  })
  // Machine Sentry Action has the same permission as Plant Event for now
  const canEditMachineSentryAction = hasPermission('EDIT_PLANT_EVENTS')

  const handleEditClick = useCallback(() => {
    onEditTaskClick(task.id)
  }, [onEditTaskClick, task.id])

  const [createMaintenanceNotificationFailed, setCreateMaintenanceNotificationFailed] =
    useState<boolean>(false)

  const [maintenanceNotificationCreated, setMaintenanceNotificationCreated] =
    useState<boolean>(false)

  const onSuccessCreateMaintenanceNotification = useCallback(() => {
    setCreateMaintenanceNotificationFailed(false)
    setMaintenanceNotificationCreated(true)
  }, [])

  const onErrorCreateMaintenanceNotification = useCallback(() => {
    setCreateMaintenanceNotificationFailed(true)
  }, [])

  const createMachineSentryNotification = useCreateMachineSentryActionMaintenanceNotification(
    {
      plantUpmId: upmPlant?.upmId,
      externalId: task.externalId
    },
    {
      onError: onErrorCreateMaintenanceNotification,
      onSuccess: onSuccessCreateMaintenanceNotification
    }
  )

  const [maintenanceNotificationDialogShown, setMaintenanceNotificationDialogShown] =
    useState<boolean>(false)

  const menuActions = useMemo(() => {
    const actions: TaskMenuOption[] = []
    if (canEditMachineSentryAction) {
      actions.push({
        key: 'edit',
        label: t('task.action.edit'),
        onClick: handleEditClick,
        dataTestId: 'task-details-edit-button',
        icon: <TaskEditIcon />
      })
    }

    const sapInfo = data?.sapInfo
    const isSapInSubmitState = sapInfo?.sapStatusCode === SapStatusCode.SUBMITTED
    const isAlreadySapConverted = !!sapInfo?.sapStatusCode && !isSapInSubmitState
    const isTimedOut =
      sapInfo &&
      hasMaintenanceNotificationCreationTimedOut(sapInfo, moment.tz(upmPlant?.timezone ?? 'UTC'))
    const isSendDisabled =
      isAlreadySapConverted ||
      isSapInSubmitState ||
      createMachineSentryNotification.isPending ||
      createMaintenanceNotificationFailed ||
      maintenanceNotificationCreated

    if (!isAlreadySapConverted || isTimedOut) {
      actions.push({
        key: 'send-maintenance-notification',
        dataTestId: 'task-details-send-maintenance-notification',
        icon: <TaskCreateMaintenanceNotificationIcon />,
        label: t('task.action.sendMaintenanceNotification'),
        onClick: () => setMaintenanceNotificationDialogShown(true),
        disabled: isSendDisabled && !isTimedOut
      })
    }
    return actions
  }, [
    canEditMachineSentryAction,
    t,
    handleEditClick,
    data?.sapInfo,
    upmPlant?.timezone,
    createMachineSentryNotification.isPending,
    createMaintenanceNotificationFailed,
    maintenanceNotificationCreated
  ])

  const hasMenuActions = !!menuActions.length

  return (
    <DataContentWrapper
      data={data}
      showLoader={isLoading}
      isError={isError}
      retryFunction={refetch}
      isRefetching={isRefetching}
      noDataSettings={{
        noDataMessage: t('task.message.taskNotFound'),
        onActionClick: () => onClose()
      }}
    >
      {data && upmPlant && (
        <TaskDetailsCardLayout
          title={data.title}
          onClose={onClose}
          metaAnnotations={
            <MachineSentryActionMetaAnnotations
              createdOn={data.createdOn}
              lastModified={data.lastModified}
              timezone={upmPlant.timezone}
            />
          }
          {...(hasMenuActions && {
            menuActions: <TaskMenuActions options={menuActions} />
          })}
        >
          <MachineSentrySapMaintenanceNotificationDetails
            item={data.sapInfo}
            plantId={task.plantId}
            notificationFailed={createMaintenanceNotificationFailed}
            timezone={upmPlant.timezone}
          />
          <MachineSentryActionDetails
            machineSentryAction={data}
            plantId={task.plantId}
            plantName={upmPlant.name}
            timezone={upmPlant.timezone}
          />
          <CommentsContainer
            plantId={task.plantId}
            commentCategoryInstanceId={data.id}
            queryKeyPrefix={MachineSentryCommentsQueryKeyPrefix}
            commentsCategory={CommentsCategory.MachineSentryActions}
          />
          {maintenanceNotificationDialogShown && (
            <MachineSentryCreateMaintenanceNotificationDialog
              event={data}
              plantId={task.plantId}
              showDialog={maintenanceNotificationDialogShown}
              setDialog={setMaintenanceNotificationDialogShown}
              createNotification={createMachineSentryNotification}
            />
          )}
        </TaskDetailsCardLayout>
      )}
    </DataContentWrapper>
  )
}
